import * as React from 'react';
import {
  TooltipTrigger,
  Tooltip,
  Button as RACButton,
  OverlayArrow,
} from 'react-aria-components';
import { DownSmallLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret Sidebar (handoff §4.3).
 * 264 px expanded / 64 px rail. Nav items 36 px, r-2, 20 px icons, 8 px icon–label gap.
 * Active item = fill-style icon + body-medium + --tvl-selected + 2 px claret inset indicator on leading edge.
 * RAC Tooltip for collapsed nav item labels — no Radix Tooltip.
 */

interface SidebarContextValue {
  collapsed: boolean;
}

const SidebarContext = React.createContext<SidebarContextValue>({ collapsed: false });

export interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  collapsed?: boolean;
}

export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  ({ className, collapsed = false, children, ...props }, ref) => (
    <SidebarContext.Provider value={{ collapsed }}>
      <nav
        ref={ref}
        className={cn(
          // Constant horizontal padding so a nav item's icon sits at the same x
          // (rail centre) whether collapsed or expanded — only the label reveals
          // on expand, the icon never jumps.
          'flex h-full w-full flex-col gap-1 overflow-y-auto px-3 py-3',
          className,
        )}
        data-collapsed={collapsed ? 'true' : 'false'}
        {...props}
      >
        {children}
      </nav>
    </SidebarContext.Provider>
  ),
);
Sidebar.displayName = 'Sidebar';

export interface SidebarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export const SidebarGroup = React.forwardRef<HTMLDivElement, SidebarGroupProps>(
  ({ className, label, collapsible = false, defaultOpen = true, children, ...props }, ref) => {
    const { collapsed } = React.useContext(SidebarContext);
    const [open, setOpen] = React.useState(defaultOpen);

    return (
      <div ref={ref} className={cn('flex flex-col gap-0.5 py-1', className)} {...props}>
        {label && !collapsed ? (
          collapsible ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className={cn(
                'flex items-center justify-between px-2 py-1',
                'text-[11px] font-[500] uppercase tracking-wider',
                'text-[var(--tvl-ink-3)] hover:text-[var(--tvl-ink-2)]',
                'outline-none',
                'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
              )}
              aria-expanded={open}
            >
              <span>{label}</span>
              <DownSmallLine
                size={12}
                color="currentColor"
                aria-hidden
                style={{
                  transition: `transform var(--tvl-d-fast)`,
                  transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
              />
            </button>
          ) : (
            <div className="px-2 py-1 text-[11px] font-[500] uppercase tracking-wider text-[var(--tvl-ink-3)]">
              {label}
            </div>
          )
        ) : null}

        {collapsible ? (
          <div className={cn(open ? 'flex flex-col gap-0.5' : 'hidden')}>{children}</div>
        ) : (
          <div className="flex flex-col gap-0.5">{children}</div>
        )}
      </div>
    );
  },
);
SidebarGroup.displayName = 'SidebarGroup';

export interface SidebarItemProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> {
  icon?: React.ReactNode;
  label: React.ReactNode;
  badge?: React.ReactNode;
  active?: boolean;
  to?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  /** Optional render override — pass NavLink from react-router-dom */
  asChild?: boolean;
  children?: React.ReactNode;
  /** #1: per-icon hover animation — Tailwind `group-hover:*` transform class(es) applied to the
   *  icon (e.g. a gear turns, a mailbox lifts, a bell swings). Transition-based, so it plays once
   *  on hover and settles back on leave (no perpetual motion). Defaults to a subtle grow when omitted. */
  iconHoverClass?: string;
}

export const SidebarItem = React.forwardRef<HTMLElement, SidebarItemProps>(
  ({ className, icon, label, badge, active = false, to, onClick, iconHoverClass, ...props }, ref) => {
    const { collapsed } = React.useContext(SidebarContext);

    const baseClass = cn(
      // §4.3: 36 px height, r-2, 8 px icon–label gap
      'group relative flex h-9 items-center gap-2 rounded-[var(--tvl-r2)]',
      'text-[14px] leading-[20px]',
      'transition-colors duration-[var(--tvl-d-fast)]',
      'outline-none',
      'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
      active
        ? 'bg-[var(--tvl-selected)] font-[500] text-[var(--tvl-ink)]'
        : 'font-normal text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
      // Constant px in both states: with the nav's px-3, the 20px icon centres in
      // the 64px rail when collapsed AND keeps the same x when expanded (no jump).
      'px-2.5',
      className,
    );

    const content = (
      <>
        {/* 2 px claret inset indicator on leading edge when active + expanded */}
        {active && !collapsed ? (
          <span
            aria-hidden
            className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r bg-[var(--tvl-accent)]"
          />
        ) : null}

        {icon ? (
          // §4.3: 20 px icons — hover feedback so hovering an item is clearly visible (the row's
          // wash-hover alone was near-invisible). #1: gear/settings items SPIN on hover (a fixed
          // rotate can vanish into a gear's own symmetry); every other item does a subtle grow.
          <span
            className={cn(
              'inline-flex h-5 w-5 shrink-0 items-center justify-center transition-transform duration-300 ease-out',
              iconHoverClass ?? 'group-hover:scale-110',
            )}
          >
            {icon}
          </span>
        ) : null}

        {!collapsed ? (
          <>
            <span className="flex-1 truncate">{label}</span>
            {badge !== undefined && badge !== null ? (
              <span
                className={cn(
                  'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5',
                  'bg-[var(--tvl-accent)] text-[10px] font-[600] text-[var(--tvl-on-accent)]',
                )}
              >
                {badge}
              </span>
            ) : null}
          </>
        ) : null}
      </>
    );

    const inner = to ? (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={to}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
        className={baseClass}
        data-pop
        aria-current={active ? 'page' : undefined}
        {...props}
      >
        {content}
      </a>
    ) : (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        className={cn(baseClass, 'w-full text-left')}
        aria-current={active ? 'page' : undefined}
      >
        {content}
      </button>
    );

    if (collapsed) {
      return (
        <TooltipTrigger delay={150} closeDelay={0}>
          {inner}
          <Tooltip
            placement="right"
            offset={8}
            className={cn(
              'z-[var(--tvl-z-tooltip)] rounded-[var(--tvl-r2)]',
              'bg-[var(--tvl-ink)] px-2 py-1',
              'text-[12px] leading-[16px] text-[var(--tvl-on-accent)] shadow-[var(--tvl-e2)]',
              'entering:animate-in entering:fade-in-0',
              'exiting:animate-out exiting:fade-out-0',
            )}
          >
            {/* No arrow per §10.8 Popover "no arrow" */}
            <span className="flex items-center gap-2">
              {label}
              {badge !== undefined && badge !== null ? (
                <span
                  className={cn(
                    'inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1',
                    'bg-[var(--tvl-accent)] text-[10px] font-[600] text-[var(--tvl-on-accent)]',
                  )}
                >
                  {badge}
                </span>
              ) : null}
            </span>
          </Tooltip>
        </TooltipTrigger>
      );
    }

    return inner;
  },
);
SidebarItem.displayName = 'SidebarItem';
